import { useMemo } from 'react';
import { css } from '@emotion/react';
import { Icon, IconName } from '@frontend/icons';
import { formatters } from '@frontend/shared';
import { theme } from '@frontend/theme';
import { Button, SkeletonLoader, Text } from '@frontend/design-system';
import { AnalyticsInfoPopover, AnalyticsInfoPopoverProps } from './info-popover';

type CardSize = 'default' | 'large';
export interface DataCardProps {
  size?: CardSize;
  label: string;
  value: string | number;
  iconData?: {
    name: IconName;
    color: string;
    bgColor: string;
  };
  ctaConfig?: {
    icon?: IconName;
    text: string;
    onClick: () => void;
  };
  infoTipData?: AnalyticsInfoPopoverProps;
  isLoading?: boolean;
}

export const DataCard = ({
  size = 'default',
  label,
  value,
  iconData,
  ctaConfig,
  infoTipData,
  isLoading,
}: DataCardProps) => {
  const formattedValue = useMemo(() => {
    switch (typeof value) {
      case 'number':
        return formatters.value(value);
      case 'string':
        if (Number.isNaN(Number(value))) {
          return value;
        } else {
          return formatters.value(Number(value));
        }
      default:
        return value;
    }
  }, [value]);
  return (
    <div css={styles.card({ size })}>
      <div css={styles.header}>
        <Text css={styles.label({ size })} weight='bold'>
          {label}
          {infoTipData && (
            <span style={{ display: 'inline-flex', verticalAlign: 'sub', marginLeft: theme.spacing(0.5) }}>
              <AnalyticsInfoPopover {...infoTipData} />
            </span>
          )}
        </Text>
        {iconData && (
          <span css={styles.icon({ color: iconData.color, bgColor: iconData.bgColor })}>
            <Icon name={iconData.name} size={16} />
          </span>
        )}
      </div>

      <div css={styles.footer}>
        {isLoading ? (
          <SkeletonLoader animation='shimmer' shape='rectangle' width='40%' height={40} />
        ) : (
          <Text css={styles.value({ size })} weight='bold'>
            {formattedValue}
          </Text>
        )}
        {!isLoading && ctaConfig && (
          <Button
            variant='tertiary'
            onClick={ctaConfig.onClick}
            css={{
              height: theme.spacing(3.5),
              padding: theme.spacing(0.75, 1),
            }}
          >
            {ctaConfig.icon && <Icon name={ctaConfig.icon} size={14} />}
            <Text as='span' size='small' color='primary'>
              {ctaConfig.text}
            </Text>
          </Button>
        )}
      </div>
    </div>
  );
};

const styles = {
  card: ({ size }: { size: CardSize }) => css`
    background-color: ${theme.colors.white};
    border: 1px solid ${theme.colors.neutral20};
    border-radius: ${theme.spacing(1)};
    padding: ${size === 'large' ? theme.spacing(2) : theme.spacing(1.5)};
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    gap: ${size === 'large' ? theme.spacing(2) : theme.spacing(1)};
    flex: 1;
    min-width: 200px;
  `,
  icon: ({ color, bgColor }: { color: string; bgColor: string }) => css`
    background-color: ${bgColor};
    color: ${color};
    display: flex;
    align-items: center;
    justify-content: center;
    width: ${theme.spacing(3)};
    height: ${theme.spacing(3)};
    border-radius: ${theme.spacing(0.5)};
    flex-shrink: 0;
  `,
  header: css`
    display: flex;
    justify-content: space-between;
    align-items: center;
  `,
  label: ({ size }: { size: CardSize }) => css`
    font-size: ${size === 'large' ? theme.fontSize(16) : theme.fontSize(14)};
  `,
  footer: css`
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
  `,
  value: ({ size }: { size: CardSize }) => css`
    font-size: ${size === 'large' ? theme.fontSize(36) : theme.fontSize(24)};
    line-height: ${size === 'large' ? theme.spacing(5) : 1.25};
  `,
  ctaButton: css`
    padding: 0;
    height: auto;
    font-size: ${theme.fontSize(14)};
    color: ${theme.colors.primary50};
    &:hover {
      background-color: transparent;
      text-decoration: underline;
    }
  `,
};
